import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import tesoreriaFlujoFondosService, {
  FlujoFondosConfig,
  FlujoFondosPeriodo,
  FlujoFondosProyeccion,
  FlujoFondosVista,
  FUENTE_LABELS,
} from '../../services/tesoreriaFlujoFondosService';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import FlujoFondosKpisBar from './flujoFondos/FlujoFondosKpisBar';
import FlujoFondosChart from './flujoFondos/FlujoFondosChart';
import FlujoFondosPeriodosTable from './flujoFondos/FlujoFondosPeriodosTable';
import FlujoFondosDrilldownDrawer from './flujoFondos/FlujoFondosDrilldownDrawer';
import FlujoFondosConfigDialog from './flujoFondos/FlujoFondosConfigDialog';
import FlujoFondosPresupuestadosDialog from './flujoFondos/FlujoFondosPresupuestadosDialog';

interface FiltrosStored {
  vista?: FlujoFondosVista;
  periodos?: number;
  fechaDesde?: string;
}

const FlujoFondosPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const stored = loadTesoreriaFiltros<FiltrosStored>(TESORERIA_FILTROS_KEYS.flujoFondos);

  const [vista, setVista] = useState<FlujoFondosVista>(
    () => (storedString(stored, 'vista', 'semanal') as FlujoFondosVista) || 'semanal'
  );
  const [periodos, setPeriodos] = useState(() => storedNumber(stored, 'periodos', 12));
  const [fechaDesde, setFechaDesde] = useState(
    () => storedString(stored, 'fechaDesde', format(new Date(), 'yyyy-MM-dd'))
  );
  const [config, setConfig] = useState<FlujoFondosConfig | null>(null);
  const [proyeccion, setProyeccion] = useState<FlujoFondosProyeccion | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [presupOpen, setPresupOpen] = useState(false);
  const [drillPeriodo, setDrillPeriodo] = useState<FlujoFondosPeriodo | null>(null);

  usePersistTesoreriaFiltros(TESORERIA_FILTROS_KEYS.flujoFondos, { vista, periodos, fechaDesde }, [
    vista,
    periodos,
    fechaDesde,
  ]);

  const fuentesActivas = useMemo(() => {
    if (!config) return [];
    const pairs: Array<[boolean, string]> = [
      [config.incluirSaldos, 'saldos'],
      [config.incluirCxc, 'cxc'],
      [config.incluirCxp, 'cxp'],
      [config.incluirChequesTerceros, 'chequesTerceros'],
      [config.incluirChequesPropios, 'chequesPropios'],
      [config.incluirRemitos, 'remitos'],
      [config.incluirPedidos, 'pedidos'],
      [config.incluirOrdenesCompra, 'ordenesCompra'],
      [config.incluirRecepciones, 'recepciones'],
      [config.incluirPresupuestados, 'presupuestados'],
    ];
    return pairs.filter(([on]) => on).map(([, key]) => key);
  }, [config]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, proy] = await Promise.all([
        tesoreriaFlujoFondosService.getConfig(),
        tesoreriaFlujoFondosService.getProyeccion({
          vista,
          periodos,
          fechaDesde,
        }),
      ]);
      setConfig(cfg);
      setProyeccion(proy);
    } catch {
      enqueueSnackbar('No se pudo cargar la proyección de flujo de fondos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [vista, periodos, fechaDesde, enqueueSnackbar]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const handleSaveConfig = async (next: FlujoFondosConfig, paraUsuario: boolean) => {
    setSavingConfig(true);
    try {
      const saved = await tesoreriaFlujoFondosService.saveConfig(next, paraUsuario);
      setConfig(saved);
      setConfigOpen(false);
      enqueueSnackbar(paraUsuario ? 'Preferencias guardadas' : 'Configuración de empresa guardada', {
        variant: 'success',
      });
      await cargar();
    } catch {
      enqueueSnackbar('No se pudo guardar la configuración', { variant: 'error' });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleExport = () => {
    if (!proyeccion) return;
    const rows = proyeccion.periodosDetalle.map((p) => ({
      Periodo: p.etiqueta,
      Desde: p.desde.slice(0, 10),
      Hasta: p.hasta.slice(0, 10),
      Ingresos: p.ingresos,
      Egresos: p.egresos,
      Neto: p.neto,
      SaldoAcumulado: p.saldoAcumulado,
      AlertaNegativo: p.alertaNegativo ? 'Sí' : '',
      AlertaExcedente: p.alertaExcedente ? 'Sí' : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'FlujoFondos');
    XLSX.writeFile(wb, `flujo-fondos-${vista}-${fechaDesde}.xlsx`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={1.5}
            mb={2}
          >
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Flujo de fondos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Proyección semanal o mensual con drilldown a los movimientos que justifican cada saldo.
              </Typography>
            </Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tesoreria/informes')}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              Volver
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            mb={2}
            flexWrap="wrap"
            useFlexGap
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={vista}
              onChange={(_, v) => v && setVista(v)}
              color="primary"
            >
              <ToggleButton value="semanal">Semanal</ToggleButton>
              <ToggleButton value="mensual">Mensual</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Períodos</InputLabel>
              <Select
                label="Períodos"
                value={periodos}
                onChange={(e) => setPeriodos(Number(e.target.value))}
              >
                {[4, 6, 8, 12, 16, 24, 36, 52].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Desde"
              type="date"
              size="small"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Button startIcon={<RefreshIcon />} onClick={() => void cargar()} disabled={loading}>
              Actualizar
            </Button>
            <Button startIcon={<SettingsIcon />} variant="outlined" onClick={() => setConfigOpen(true)}>
              Fuentes
            </Button>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setPresupOpen(true)}>
              Presupuestado
            </Button>
            <Button
              startIcon={<ExportIcon />}
              variant="outlined"
              onClick={handleExport}
              disabled={!proyeccion}
            >
              Excel
            </Button>
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={2.5}>
            {fuentesActivas.map((f) => (
              <Chip key={f} size="small" label={FUENTE_LABELS[f] || f} variant="outlined" />
            ))}
            {!fuentesActivas.length && !loading ? (
              <Typography variant="caption" color="text.secondary">
                Ninguna fuente activa — configurá las fuentes del informe.
              </Typography>
            ) : null}
          </Stack>

          {loading && !proyeccion ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <FlujoFondosKpisBar kpis={proyeccion?.kpis || null} />
              {proyeccion ? (
                <FlujoFondosChart
                  periodos={proyeccion.periodosDetalle}
                  umbralExcedente={proyeccion.kpis.umbralExcedente}
                  onPeriodoClick={setDrillPeriodo}
                />
              ) : null}
              {proyeccion ? (
                <FlujoFondosPeriodosTable
                  periodos={proyeccion.periodosDetalle}
                  onPeriodoClick={setDrillPeriodo}
                />
              ) : null}
              {loading ? (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Actualizando…
                </Typography>
              ) : null}
              {isMobile ? null : (
                <Typography variant="caption" color="text.secondary">
                  Los saldos en rojo indican huecos a cubrir; el chip “Invertir” marca excedentes sobre el umbral
                  configurado.
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <FlujoFondosDrilldownDrawer
        open={!!drillPeriodo}
        periodo={drillPeriodo}
        onClose={() => setDrillPeriodo(null)}
      />
      <FlujoFondosConfigDialog
        open={configOpen}
        config={config}
        saving={savingConfig}
        onClose={() => setConfigOpen(false)}
        onSave={handleSaveConfig}
      />
      <FlujoFondosPresupuestadosDialog
        open={presupOpen}
        onClose={() => setPresupOpen(false)}
        onChanged={() => void cargar()}
      />
    </Box>
  );
};

export default FlujoFondosPage;
